import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LanguageUtils } from '../../../lib/languageUtils';
import { useLanguage } from '../../hooks/useLanguage';
import { MenuIcon, XIcon } from './icons';
import ThemeToggle from './ThemeToggle';
import { BrandLogo } from './BrandLogo';
import { cn } from '@/lib/utils';

const Header: React.FC = () => {
  const { t, language } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 8;
      setIsScrolled(prev => prev === scrolled ? prev : scrolled);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const pathWithoutLang = useMemo(
    () => LanguageUtils.extractLanguageFromPath(location.pathname).pathWithoutLang,
    [location.pathname]
  );

  const isActive = (path: string): boolean => {
    if (path === '/admin') {
      return pathWithoutLang.startsWith('/admin');
    }
    if (path === '/') {
      return pathWithoutLang === '' || pathWithoutLang === '/';
    }
    return pathWithoutLang === path;
  };

  const getLocalizedPath = (path: string) => {
    return LanguageUtils.getLocalizedPath(language, path);
  };

  const handleLanguageSwitch = () => {
    const newLanguage = language === 'en' ? 'si' : 'en';
    const currentPath = location.pathname;
    const newPath = LanguageUtils.switchLanguageInUrl(currentPath, newLanguage);
    navigate(newPath);
  };

  const languageSwitchText = language === 'en' ? 'සි' : 'En';
  const languageSwitchAction = language === 'en' ? 'Switch to Sinhala' : 'Switch to English';

  const navLinkClass = (path: string) =>
    cn(
      'relative text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm px-1',
      isActive(path)
        ? 'text-primary after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-0.5 after:bg-primary after:rounded-full'
        : 'text-stone-600 hover:text-primary'
    );

  return (
    <header
      className={cn(
        'sticky top-0 z-50 w-full transition-all duration-200',
        isScrolled
          ? 'bg-card/95 backdrop-blur-sm shadow-sm border-b border-border'
          : 'bg-card'
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Brand */}
          <Link
            to={getLocalizedPath('')}
            /* saffron-700, not the usual `text-primary` (saffron-600): the
               wordmark is 18px normal-weight text on a white header, where
               saffron-600 measures 3.18:1 against the 4.5:1 WCAG AA minimum —
               the single color-contrast failure in the Lighthouse a11y audit.
               saffron-700 is 5.02:1. Scoped to the wordmark rather than moved
               into --color-primary so the rest of the palette is untouched;
               the dark-theme override of saffron-700 is a light amber, so this
               stays legible in both themes. */
            className="group flex items-center gap-2.5 text-saffron-700 font-semibold text-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          >
            <span className="samadhi-brand-mark relative grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-full border border-saffron-200 bg-card shadow-sm transition-transform duration-300 group-hover:-rotate-2 group-hover:scale-[1.03]">
              <BrandLogo
                ariaHidden
                loading="eager"
                className="samadhi-brand-logo h-full w-full object-cover"
              />
            </span>
            <span className="font-display leading-tight">{t('header.title')}</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden sm:flex items-center gap-6" aria-label="Main navigation">
            <Link to={getLocalizedPath('')} className={navLinkClass('/')}>
              {t('header.nav.home')}
            </Link>
            <Link to={getLocalizedPath('events')} className={navLinkClass('/events')}>
              {t('header.nav.events')}
            </Link>
            <Link to={getLocalizedPath('offerings')} className={navLinkClass('/offerings')}>
              {t('header.nav.donate')}
            </Link>
            <Link to={getLocalizedPath('admin')} className={navLinkClass('/admin')}>
              {t('header.nav.admin')}
            </Link>
            <ThemeToggle />
            <button
              onClick={handleLanguageSwitch}
              className="px-3 py-1.5 rounded-full border border-border text-sm font-medium text-stone-600 hover:bg-stone-50 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`${languageSwitchAction} (${languageSwitchText})`}
            >
              {t(language === 'en' ? 'header.langSwitch.si' : 'header.langSwitch.en')}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="sm:hidden flex items-center gap-2">
            <ThemeToggle compact />
            <button
              onClick={handleLanguageSwitch}
              className="px-2.5 py-1 rounded-full border border-border text-xs font-medium text-stone-600 hover:bg-stone-50"
              aria-label={`${languageSwitchAction} (${languageSwitchText})`}
            >
              {t(language === 'en' ? 'header.langSwitch.si' : 'header.langSwitch.en')}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-stone-600 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm p-1"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMenuOpen ? (
                <XIcon className="w-5 h-5" aria-hidden="true" />
              ) : (
                <MenuIcon className="w-5 h-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Drawer */}
        <div
          id="mobile-menu"
          data-testid="mobile-menu"
          className={cn(
            'sm:hidden overflow-hidden transition-all duration-200 ease-in-out',
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          )}
          aria-hidden={!isMenuOpen}
          {...(!isMenuOpen ? { inert: '' } : {})}
        >
          <nav className="flex flex-col gap-1 pb-4 pt-2 border-t border-border" aria-label="Mobile navigation">
            {[
              { path: '/', label: t('header.nav.home') },
              { path: '/events', label: t('header.nav.events') },
              { path: '/offerings', label: t('header.nav.donate') },
              { path: '/admin', label: t('header.nav.admin') },
            ].map(({ path, label }) => (
              <Link
                key={path}
                to={getLocalizedPath(path === '/' ? '' : path.slice(1))}
                onClick={() => setIsMenuOpen(false)}
                className={cn(
                  'px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive(path)
                    ? 'bg-saffron-50 text-primary'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-primary'
                )}
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
